// The report creation flow (plan.md §4.2).
//
// A report is category, location and time. There is no description field and
// no email field — deliberately, and permanently (plan.md §3). The two things
// this screen must get right beyond that:
//
//   1. The emergency notice is persistent and non-dismissable, on every
//      report regardless of category (plan.md §3, "Not an emergency service").
//      The number itself is region-specific and comes from deployment config
//      (public_config.emergency_number), so this screen never hardcodes it.
//   2. Once a category is chosen, the pin shows the precision that will
//      actually be stored, so nobody is misled about what they are
//      publishing (plan.md §4.2).

import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { i18n } from '../lib/i18n.js';
import CategoryPicker from './CategoryPicker.jsx';
import { iconFor } from '../lib/icons.js';
import { createReport } from '../lib/db.js';
import { getSessionToken } from '../lib/session.js';
import { relativeTime } from '../lib/format.js';

export default function ReportComposer({
  taxonomy, draftPin, onDraftMove, onClose, onCreated, onShowExisting, emergencyNumber,
}) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [duplicate, setDuplicate] = useState(null);

  function handlePick({ category, collection }) {
    setPicked({ category, collection });
    setDuplicate(null);
    setError(null);
    // Surface the real precision on the map the moment it is known.
    onDraftMove({ ...draftPin, precisionM: collection.location_precision_m });
  }

  async function submit() {
    if (!picked || !draftPin) return;
    setSubmitting(true);
    setError(null);

    try {
      const result = await createReport({
        categoryId: picked.category.id,
        lat: draftPin.lat,
        lng: draftPin.lng,
        sessionToken: getSessionToken(),
      });

      if (result.status === 'created') {
        onCreated(result);
      } else if (result.status === 'duplicate') {
        // Not an error state. Something matching is already on the map, so
        // offer to confirm it instead of filing a second one (plan.md OQ1).
        setDuplicate(result);
      } else if (result.status === 'rate_limited') {
        const mins = Math.ceil((result.retry_after ?? 3600) / 60);
        setError(t('composer.rateLimited', { count: mins }));
      } else {
        setError(errorMessage(result.reason));
      }
    } catch (err) {
      setError(err.message ?? t('composer.errFallback'));
    } finally {
      setSubmitting(false);
    }
  }

  const precision = picked?.collection.location_precision_m ?? 0;

  // The picker needs the full-height sheet to scroll 122 categories. Once a
  // category is chosen the sheet must shrink to its content, or it covers the
  // map — which is exactly when the user needs to see the pin and the
  // precision halo the copy is telling them about.
  const tall = !picked && !duplicate;

  return (
    <div
      className={`sheet${tall ? ' sheet--tall' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={t('composer.aria')}
    >
      <header className="sheet__header">
        <h2 className="sheet__title">{t('composer.title')}</h2>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label={t('nav.close')}>×</button>
      </header>

      {/* Persistent and non-dismissable, on every report (plan.md §3). */}
      <p className="emergency-notice" role="note">
        <Trans
          i18nKey="composer.emergencyNotice"
          components={{ strong: <strong /> }}
          values={{ number: emergencyNumber ?? '999' }}
        />
      </p>

      {duplicate ? (
        <div className="sheet__body">
          <div className="duplicate">
            <h3 className="duplicate__title">{t('composer.duplicateTitle')}</h3>
            <p className="duplicate__body">
              <Trans
                i18nKey="composer.duplicateBody"
                components={{ strong: <strong /> }}
                values={{ category: picked.category.name, time: relativeTime(duplicate.created_at) }}
              />
              {duplicate.corroboration_count > 0 && (
                <Trans
                  i18nKey={duplicate.corroboration_count === 1
                    ? 'composer.duplicateConfirmedOne'
                    : 'composer.duplicateConfirmedOther'}
                  values={{ count: duplicate.corroboration_count }}
                />
              )}.
            </p>
            <p className="duplicate__body duplicate__body--dim">
              {t('composer.duplicateHint')}
            </p>
            <div className="sheet__actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => onShowExisting(duplicate.report_id)}
              >
                {t('composer.duplicateSeeIt')}
              </button>
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                {t('composer.duplicateNeverMind')}
              </button>
            </div>
          </div>
        </div>
      ) : !picked ? (
        <div className="sheet__body sheet__body--flush">
          <CategoryPicker taxonomy={taxonomy} onPick={handlePick} />
        </div>
      ) : (
        <div className="sheet__body">
          <button type="button" className="chosen" onClick={() => setPicked(null)}>
            <span className="chosen__icon" aria-hidden="true">
              {iconFor(picked.category.icon, picked.collection.icon)}
            </span>
            <span className="chosen__text">
              <span className="chosen__name">{picked.category.name}</span>
              <span className="chosen__collection">{picked.collection.name}</span>
            </span>
            <span className="chosen__change">{t('composer.change')}</span>
          </button>

          <div className="precision-note">
            {precision > 0 ? (
              <Trans
                i18nKey="composer.precisionApprox"
                components={{ strong: <strong /> }}
                values={{ m: precision }}
              />
            ) : (
              <Trans
                i18nKey="composer.precisionExact"
                components={{ strong: <strong /> }}
              />
            )}
          </div>

          <p className="hint">
            {t('composer.hint')}
          </p>

          {error && <p className="error" role="alert">{error}</p>}

          <div className="sheet__actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? t('composer.filing') : t('composer.file')}
            </button>
            <button type="button" className="btn btn--ghost" onClick={onClose}>{t('composer.cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function errorMessage(reason) {
  switch (reason) {
    case 'invalid_location': return i18n.t('composer.errInvalidLocation');
    case 'invalid_session':  return i18n.t('composer.errInvalidSession');
    case 'unknown_category': return i18n.t('composer.errUnknownCategory');
    case 'retired_category': return i18n.t('composer.errRetiredCategory');
    default:                 return i18n.t('composer.errDefault');
  }
}
